import apiClient from './config';
import { MenuSection, MenuItem } from '../types/menu';
import { Restaurant } from '../types/restaurant';
import { buildQueryString, handleApiError } from './utils';
import { getCurrentLanguage, SupportedLanguage } from '../config/languageConfig';
import { createCacheKey, getCacheData, setCacheData } from './cacheUtils';
import { getCurrencyFromLanguage, getPriceFieldByCurrency } from '../utils/currencyUtils';
import { getTranslation } from '../config/translations';

/**
 * 가게의 메뉴 목록 조회
 * @param restaurantId 가게 ID
 * @param lang 언어 설정 (기본값: 현재 설정된 언어)
 * @returns 메뉴 섹션 목록
 */
export const getRestaurantMenus = async (
  restaurantId: string | number,
  lang?: string
): Promise<MenuSection[]> => {
  const endpoint = `/api/v1/stores/${restaurantId}/menus`;
  const currentLang = lang || getCurrentLanguage();
  
  try {
    // 쿼리 파라미터와 엔드포인트
    const params = { lang: currentLang };
    
    // 캐시 키 생성
    const cacheKey = createCacheKey(endpoint, params);
    
    // 캐시된 데이터 확인
    const cachedData = getCacheData<MenuSection[]>(cacheKey, currentLang as SupportedLanguage);
    if (cachedData) {
      return cachedData;
    }
    
    // 언어 설정 쿼리 파라미터 추가
    const queryString = buildQueryString(params);
    const response = await apiClient.get(`${endpoint}${queryString}`);
    
    // API 응답을 애플리케이션 데이터 구조로 변환
    const menuData = transformMenuData(response.data, currentLang as SupportedLanguage);
    
    // 변환된 데이터 캐싱
    setCacheData(cacheKey, menuData, currentLang as SupportedLanguage);
    
    return menuData;
  } catch (error) {
    const apiError = handleApiError(error);
    console.error(`Error fetching restaurant menus: ${apiError.message}`, apiError);
    
    // 에러 발생 시 빈 배열 반환
    return [];
  }
};

/**
 * 메뉴 상세 정보 조회
 * @param restaurantId 가게 ID
 * @param menuId 메뉴 ID
 * @param lang 언어 설정 (기본값: 현재 설정된 언어)
 * @returns 메뉴 상세 정보
 */
export const getMenuDetail = async (
  restaurantId: string | number,
  menuId: string | number,
  lang?: string
): Promise<MenuItem | null> => {
  const endpoint = `/api/v1/stores/${restaurantId}/menus/${menuId}`;
  const currentLang = lang || getCurrentLanguage();
  
  try {
    // 쿼리 파라미터와 엔드포인트
    const params = { lang: currentLang };
    
    // 캐시 키 생성
    const cacheKey = createCacheKey(endpoint, params);
    
    // 캐시된 데이터 확인
    const cachedData = getCacheData<MenuItem>(cacheKey, currentLang as SupportedLanguage);
    if (cachedData) {
      return cachedData;
    }
    
    // 언어 설정 쿼리 파라미터 추가
    const queryString = buildQueryString(params);
    const response = await apiClient.get(`${endpoint}${queryString}`);
    // API 응답을 애플리케이션 데이터 구조로 변환
    const menuItem = transformMenuItemData(response.data, currentLang as SupportedLanguage);
    
    // 변환된 데이터 캐싱
    setCacheData(cacheKey, menuItem, currentLang as SupportedLanguage);
    
    return menuItem;
  } catch (error) {
    const apiError = handleApiError(error);
    console.error(`Error fetching menu detail: ${apiError.message}`, apiError);
    
    // 에러 발생 시 null 반환
    return null;
  }
};

/**
 * API 응답 데이터를 애플리케이션에서 사용하는 MenuItem 형식으로 변환
 * @param apiMenuItem API에서 반환된 메뉴 아이템 데이터
 * @param lang 언어 설정
 * @returns 변환된 MenuItem 객체
 */
function transformMenuItemData(apiMenuItem: any, lang: SupportedLanguage = 'en'): MenuItem {
  if (!apiMenuItem || typeof apiMenuItem !== 'object') {
    throw new Error('Invalid menu item data');
  }

  // 언어에 맞는 통화 및 가격 필드 결정
  const currency = getCurrencyFromLanguage(lang);
  const priceField = getPriceFieldByCurrency(currency);
  
  // 기본 가격 또는 언어별 가격 사용
  const price = apiMenuItem[priceField] !== undefined ? apiMenuItem[priceField] : apiMenuItem.price || 0;
  
  // 옵션 변환
  const options = Array.isArray(apiMenuItem.options) ? apiMenuItem.options.map((opt: { id: string; name: string; description?: string }) => ({
    id: opt.id,
    name: opt.name,
    slug: opt.id,
    sourceId: 0,
    mandatory: false,
    multiple: false,
    multipleCount: 1,
    isAvailableQuantity: false,
    menuOptionItems: [{
      id: opt.id,
      name: opt.name,
      slug: opt.id,
      sourceId: 0,
      price: 0,
      description: opt.description || '',
      isDeposit: false,
      depositPrice: 0,
      depositDescription: ''
    }]
  })) : [];

  // 메뉴 아이템 변환
  return {
    id: apiMenuItem.id,
    name: apiMenuItem.name,
    subtitle: "",
    price: price,
    description: apiMenuItem.description || "",
    image: apiMenuItem.image || null,
    originalImage: apiMenuItem.image || null,
    slug: apiMenuItem.id,
    sourceId: 0,
    reviewCount: 0,
    oneDish: false,
    menuOptions: options,
    // available 필드가 명시적으로 false인 경우에만 품절 처리 (없으면 판매 가능으로 간주)
    available: apiMenuItem.available !== false,
    nameEn: apiMenuItem.nameEn || apiMenuItem.name, // 영문 이름이 없는 경우 기본 이름 사용
    descriptionEn: apiMenuItem.descriptionEn || apiMenuItem.description, // 영문 설명이 없는 경우 기본 설명 사용
    // 환율에 따른 가격 정보 추가
    priceKrw: apiMenuItem.priceKrw,
    priceUsd: apiMenuItem.priceUsd,
    priceJpy: apiMenuItem.priceJpy,
    priceCny: apiMenuItem.priceCny,
    priceTwd: apiMenuItem.priceTwd,
    // 프로모션 관련 필드 추가
    promotionRatio: apiMenuItem.promotionRatio,
    promotionPrice: apiMenuItem.promotionPrice,
    promotionPriceKrw: apiMenuItem.promotionPriceKrw,
    promotionPriceUsd: apiMenuItem.promotionPriceUsd,
    promotionPriceJpy: apiMenuItem.promotionPriceJpy,
    promotionPriceCny: apiMenuItem.promotionPriceCny,
    promotionPriceTwd: apiMenuItem.promotionPriceTwd,
    isPromotionKrw: apiMenuItem.isPromotionKrw,
    isPromotionUsd: apiMenuItem.isPromotionUsd,
    isPromotionJpy: apiMenuItem.isPromotionJpy,
    isPromotionCny: apiMenuItem.isPromotionCny,
    isPromotionTwd: apiMenuItem.isPromotionTwd
  };
}

/**
 * API 응답 데이터를 애플리케이션에서 사용하는 MenuSection 형식으로 변환
 * @param apiMenus API에서 반환된 메뉴 데이터
 * @param lang 언어 설정
 * @returns 변환된 MenuSection 배열
 */
function transformMenuData(apiMenus: any[], lang: SupportedLanguage = 'en'): MenuSection[] {
  if (!Array.isArray(apiMenus) || apiMenus.length === 0) {
    return [];
  }
  
  // 언어에 맞는 통화 및 가격 필드 결정
  const currency = getCurrencyFromLanguage(lang);
  const priceField = getPriceFieldByCurrency(currency);
  
  // 메뉴 섹션별로 그룹화
  const menusBySection: Record<string, MenuItem[]> = {};
  
  // API 응답의 각 메뉴 아이템을 변환하여 섹션별로 그룹화
  apiMenus.forEach(item => {
    const sectionName = item.section || 'Other';
    
    if (!menusBySection[sectionName]) {
      menusBySection[sectionName] = [];
    }
    
    // 기본 가격 또는 언어별 가격 사용
    const price = item[priceField] !== undefined ? item[priceField] : item.price || 0;
    
    // 옵션 변환
    const options = Array.isArray(item.options) ? item.options.map((opt: { id: string; name: string; description?: string }) => ({
      id: opt.id,
      name: opt.name,
      slug: opt.id,
      sourceId: 0,
      mandatory: false,
      multiple: false,
      multipleCount: 1,
      isAvailableQuantity: false,
      menuOptionItems: [{
        id: opt.id,
        name: opt.name,
        slug: opt.id,
        sourceId: 0,
        price: 0,
        description: opt.description || '',
        isDeposit: false,
        depositPrice: 0,
        depositDescription: ''
      }]
    })) : [];
    
    // 메뉴 아이템 변환
    const menuItem: MenuItem = {
      id: item.id,
      name: item.name,
      subtitle: "",
      price: price,
      description: item.description || "",
      image: item.image || null,
      originalImage: item.image || null,
      slug: item.id,
      sourceId: 0,
      reviewCount: 0,
      oneDish: false,
      menuOptions: options,
      // available 필드가 명시적으로 false인 경우에만 품절 처리 (없으면 판매 가능으로 간주)
      available: item.available !== false,
      nameEn: item.nameEn || item.name, // 영문 이름이 없는 경우 기본 이름 사용
      descriptionEn: item.descriptionEn || item.description, // 영문 설명이 없는 경우 기본 설명 사용
      // 환율에 따른 가격 정보 추가
      priceKrw: item.priceKrw,
      priceUsd: item.priceUsd,
      priceJpy: item.priceJpy,
      priceCny: item.priceCny,
      priceTwd: item.priceTwd,
      // 프로모션 관련 필드 추가
      promotionRatio: item.promotionRatio,
      promotionPrice: item.promotionPrice,
      promotionPriceKrw: item.promotionPriceKrw,
      promotionPriceUsd: item.promotionPriceUsd,
      promotionPriceJpy: item.promotionPriceJpy,
      promotionPriceCny: item.promotionPriceCny,
      promotionPriceTwd: item.promotionPriceTwd,
      isPromotionKrw: item.isPromotionKrw,
      isPromotionUsd: item.isPromotionUsd,
      isPromotionJpy: item.isPromotionJpy,
      isPromotionCny: item.isPromotionCny,
      isPromotionTwd: item.isPromotionTwd
    };
    
    menusBySection[sectionName].push(menuItem);
  });
  
  // 그룹화된 메뉴를 MenuSection 배열로 변환
  let menuSections: MenuSection[] = Object.entries(menusBySection).map(([sectionName, items], index) => ({
    id: `section-${index}`,
    name: sectionName,
    nameEn: sectionName,
    image: null,
    msType: sectionName,
    slug: sectionName.toLowerCase().replace(/\s+/g, '-'),
    sourceId: index,
    menuItems: items
  }));
  
  // 하단에 배치할 섹션 이름들 - 다국어 지원
  // 각 섹션의 모든 언어 버전을 포함
  const bottomSectionNames = {
    sides: [
      getTranslation('section.sides', 'en'),
      getTranslation('section.sides', 'ko'),
      getTranslation('section.sides', 'ja'),
      getTranslation('section.sides', 'zh-CN'),
      getTranslation('section.sides', 'zh-TW')
    ],
    sideMenu: [
      getTranslation('section.sideMenu', 'en'),
      getTranslation('section.sideMenu', 'ko'),
      getTranslation('section.sideMenu', 'ja'),
      getTranslation('section.sideMenu', 'zh-CN'),
      getTranslation('section.sideMenu', 'zh-TW'),
      getTranslation('section.sideDish', 'en'),
      getTranslation('section.sideDish', 'ko'),
      getTranslation('section.sideDish', 'ja'),
      getTranslation('section.sideDish', 'zh-CN'),
      getTranslation('section.sideDish', 'zh-TW')
    ],
    softDrink: [
      getTranslation('section.softDrink', 'en'),
      getTranslation('section.softDrink', 'ko'),
      getTranslation('section.softDrink', 'ja'),
      getTranslation('section.softDrink', 'zh-CN'),
      getTranslation('section.softDrink', 'zh-TW')
    ],
    drinks: [
      getTranslation('section.drinks', 'en'),
      getTranslation('section.drinks', 'ko'),
      getTranslation('section.drinks', 'ja'),
      getTranslation('section.drinks', 'zh-CN'),
      getTranslation('section.drinks', 'zh-TW')
    ],
    beverages: [
      getTranslation('section.beverages', 'en'),
      getTranslation('section.beverages', 'ko'),
      getTranslation('section.beverages', 'ja'),
      getTranslation('section.beverages', 'zh-CN'),
      getTranslation('section.beverages', 'zh-TW')
    ],
    // 추가 음료 섹션들
    softDrinkVariants: [
      'Soft Drink',
      'ソフトドリンク', 
      '음료',
      '饮料',
      '飲品'
    ]
  };
  
  // 모든 하단 섹션 이름을 단일 배열로 평면화
  const bottomSections = [
    ...bottomSectionNames.sides,
    ...bottomSectionNames.sideMenu,
    ...bottomSectionNames.softDrink,
    ...bottomSectionNames.drinks,
    ...bottomSectionNames.beverages
  ];
  
  // 섹션 정렬: 하단에 배치할 섹션들을 찾아서 제거한 후 나중에 추가
  const sectionsToMoveToBottom = [];
  
  for (const sectionName of bottomSections) {
    const index = menuSections.findIndex(section => 
      section.name === sectionName || section.nameEn === sectionName
    );
    
    if (index !== -1) {
      const removedSection = menuSections.splice(index, 1)[0];
      sectionsToMoveToBottom.push(removedSection);
    }
  }
  
  // 하단에 배치할 섹션들 추가
  menuSections = [...menuSections, ...sectionsToMoveToBottom];
  
  return menuSections;
}

/**
 * 프랜차이즈 ID로 가게 검색
 * @param lat 위도
 * @param lng 경도
 * @param franchiseId 프랜차이즈 ID
 * @param limit 결과 개수 제한 (기본값: 1)
 * @returns 검색된 가게 목록
 */
export const searchRestaurantsByFranchise = async (
  lat: number,
  lng: number,
  franchiseId: string,
  limit: number = 1,
  addressId?: string
): Promise<Restaurant[]> => {
  const endpoint = '/api/v1/stores';
  const currentLang = getCurrentLanguage();
  
  try {
    const params: Record<string, string> = {
      lat: lat.toString(),
      lng: lng.toString(),
      franchiseId: franchiseId,
      limit: limit.toString(),
      lang: currentLang  // 언어 파라미터 추가
    };

    // addressId가 있을 때만 파라미터에 추가
    if (addressId && addressId.trim() !== '') {
      params.addressId = addressId;
    }
    
    const cacheKey = createCacheKey(endpoint, params);
    const cachedData = getCacheData<Restaurant[]>(cacheKey, currentLang as SupportedLanguage);
    if (cachedData) {
      return cachedData;
    }
    
    const queryString = buildQueryString(params);
    const response = await apiClient.get(`${endpoint}${queryString}`);
    const restaurants = response.data;
    setCacheData(cacheKey, restaurants, currentLang as SupportedLanguage);
    
    return restaurants;
  } catch (error) {
    const apiError = handleApiError(error);
    console.error(`Error searching restaurants by franchise: ${apiError.message}`, apiError);
    return [];
  }
};

export const searchRestaurantsByMenu = async (
  lat: number,
  lng: number,
  menuName: string,
  categoryId?: string,
  addressId?: string
): Promise<Restaurant[]> => {
  try {
    const currentLang = getCurrentLanguage(); // 현재 언어 가져오기
    
    const params: Record<string, string> = {
      lat: lat.toString(),
      lng: lng.toString(),
      menuName: menuName,
      lang: currentLang  // 언어 파라미터 추가
    };

    // categoryId가 빈 값이 아닐 때만 파라미터에 추가
    if (categoryId && categoryId.trim() !== '') {
      params.categoryId = categoryId;
    }

    // addressId가 있을 때만 파라미터에 추가
    if (addressId && addressId.trim() !== '') {
      params.addressId = addressId;
    }

    const queryString = buildQueryString(params);
    const response = await apiClient.get(`/api/v1/stores${queryString}`);
    return response.data;
  } catch (error) {
    const apiError = handleApiError(error);
    console.error('Error searching restaurants by menu:', apiError);
    throw apiError;
  }
}; 